const chapter = {
  id: 'chemical-kinetics',
  slug: 'chemical-kinetics',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Chemical Kinetics',
        hindi: 'रासायनिक बलगतिकी',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Physical Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'rate',
      title: {
        english: 'Rate of Reaction',
        hindi: 'अभिक्रिया की दर',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Rate of reaction',
        hindi: 'प्रतिक्रिया की दर',
          },
          body: {
            english: `For $aA + bB \\to cC + dD$:
$$\\text{Rate} = -\\dfrac{1}{a}\\dfrac{d[A]}{dt} = -\\dfrac{1}{b}\\dfrac{d[B]}{dt} = +\\dfrac{1}{c}\\dfrac{d[C]}{dt} = +\\dfrac{1}{d}\\dfrac{d[D]}{dt}.$$
**Units**: mol·L⁻¹·s⁻¹.`,
            hindi: '$aA + bB \\to cC + dD$ के लिए: $$\\text{Rate} = -\\dfrac{1}{a}\\dfrac{d[A]}{dt} = -\\dfrac{1}{b}\\dfrac{d[B]}{dt} = +\\dfrac{1}{c}\\dfrac{d[C]}{dt} = +\\dfrac{1}{d}\\dfrac{d[D]}{dt}.$$ **इकाइयां**: mol·L⁻¹·s⁻¹.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Average rate** = $\\Delta[X]/\\Delta t$ over a finite interval.',
        hindi: '**औसत दर** = $\\Delta[X]/\\Delta t$ एक सीमित अंतराल पर। **तात्कालिक दर** = $\\Delta[X]/\\Delta t$ के रूप में सीमा = सांद्र-समय वक्र का ढलान।',
            },
            {
              english: '**Instantaneous rate** = limit as $\\Delta t \\to 0$ = slope of conc–time curve.',
        hindi: '**तात्कालिक दर** = $\\Delta t \\to 0$ के रूप में सीमा = सांद्र-समय वक्र का ढलान।',
            },
            {
              english: 'Factors affecting rate: nature of reactants, **concentration**, **temperature**, **catalyst**, surface area, light/radiation.',
        hindi: 'दर को प्रभावित करने वाले कारक: अभिकारकों की प्रकृति, **एकाग्रता**, **तापमान**, **उत्प्रेरक**, सतह क्षेत्र, light/radiation।',
            },
          ],
        },
      ],
    },
    {
      id: 'rate-law',
      title: {
        english: 'Rate Law, Order & Molecularity',
        hindi: 'दर नियम, कोटि और आण्विकता',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Rate law',
        hindi: 'दर कानून',
          },
          statement: {
            english: '$\\text{Rate} = k[A]^x[B]^y$. The exponents $x, y$ are determined **experimentally**, not from stoichiometry. **Order** of reaction $= x + y$. Rate constant $k$ depends only on $T$ and catalyst.',
        hindi: '$\\text{Rate} = k[A]^x[B]^y$। घातांक $x, y$ को **प्रयोगात्मक रूप से** निर्धारित किया जाता है, स्टोइकोमेट्री से नहीं। प्रतिक्रिया का **आदेश** $= x + y$। दर स्थिरांक $k$ केवल $T$ और उत्प्रेरक पर निर्भर करता है। **आणविकता** = प्रारंभिक चरण में अणुओं की संख्या। हमेशा एक पूर्ण संख्या ≥ 1; कभी भी 0 या भिन्नात्मक नहीं. केवल प्राथमिक प्रतिक्रियाओं के लिए परिभाषित।',
          },
        },
        {
          type: 'table',
          headers: ['Order', 'Units of $k$'],
          rows: [
            ['0', 'mol·L⁻¹·s⁻¹'],
            ['1', 's⁻¹'],
            ['2', 'L·mol⁻¹·s⁻¹'],
            ['$n$', '$\\text{(mol/L)}^{1-n}\\,s^{-1}$'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Molecularity** = number of molecules in an elementary step. Always a whole number ≥ 1; never 0 or fractional. Defined only for elementary reactions.',
        hindi: '**आणविकता** = प्रारंभिक चरण में अणुओं की संख्या। हमेशा एक पूर्ण संख्या ≥ 1; कभी भी 0 या भिन्नात्मक नहीं. केवल प्राथमिक प्रतिक्रियाओं के लिए परिभाषित।',
            },
            {
              english: 'Order may be fractional, zero, or negative. Order $\\ne$ molecularity in general.',
        hindi: 'ऑर्डर आंशिक, शून्य या नकारात्मक हो सकता है। सामान्यतः आदेश $\\ne$ आणविकता। **दर-निर्धारण चरण (rds)** = बहु-चरणीय तंत्र में सबसे धीमा चरण; देखे गए क्रम को नियंत्रित करता है।',
            },
            {
              english: '**Rate-determining step (rds)** = slowest step in a multi-step mechanism; controls observed order.',
        hindi: '**दर-निर्धारण चरण (आरडीएस)** = बहु-चरणीय तंत्र में सबसे धीमा कदम; देखे गए क्रम को नियंत्रित करता है।',
            },
            {
              english: 'Pseudo-first-order: when one reactant is in large excess (e.g. acid hydrolysis of ester in water — water is constant).',
        hindi: 'छद्म-प्रथम-क्रम: जब एक अभिकारक बड़ी मात्रा में होता है (उदाहरण के लिए पानी में एस्टर का एसिड हाइड्रोलिसिस - पानी स्थिर होता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'integrated',
      title: {
        english: 'Integrated Rate Equations',
        hindi: 'समाकलित दर समीकरण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Zero-order',
        hindi: 'शून्य-आदेश',
          },
          statement: {
            english: 'Rate $= k$. Integrated: $[A] = [A]_0 - kt$. Half-life: $t_{1/2} = [A]_0/(2k)$ (depends on initial concentration).',
        hindi: 'दर $= k$। एकीकृत: $[A] = [A]_0 - kt$. आधा जीवन: $t_{1/2} = [A]_0/(2k)$ (प्रारंभिक एकाग्रता पर निर्भर करता है)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'First-order',
        hindi: 'प्रथम क्रम',
          },
          statement: {
            english: 'Rate $= k[A]$. Integrated: $\\ln \\dfrac{[A]_0}{[A]} = kt$ ⇔ $[A] = [A]_0 e^{-kt}$. Or $k = \\dfrac{2.303}{t}\\log\\dfrac{[A]_0}{[A]}$. **Half-life** $t_{1/2} = 0.693/k$ — independent of $[A]_0$.',
        hindi: 'दर $= k[A]$. एकीकृत: $\\ln \\dfrac{[A]_0}{[A]} = kt$ ⇔ $[A] = [A]_0 e^{-kt}$। या $k = \\dfrac{2.303}{t}\\log\\dfrac{[A]_0}{[A]}$. **आधा जीवन** $t_{1/2} = 0.693/k$ — $[A]_0$ से स्वतंत्र।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Second-order (single reactant)',
        hindi: 'दूसरा क्रम (एकल अभिकारक)',
          },
          statement: {
            english: 'Rate $= k[A]^2$. Integrated: $\\dfrac{1}{[A]} - \\dfrac{1}{[A]_0} = kt$. $t_{1/2} = 1/(k[A]_0)$.',
        hindi: 'दर $= k[A]^2$. एकीकृत: $\\dfrac{1}{[A]} - \\dfrac{1}{[A]_0} = kt$. $t_{1/2} = 1/(k[A]_0)$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Plot to determine order: zero — $[A]$ vs $t$ linear; first — $\\ln[A]$ vs $t$ linear (slope $-k$); second — $1/[A]$ vs $t$ linear.',
        hindi: 'क्रम निर्धारित करने के लिए प्लॉट: शून्य - $[A]$ बनाम $t$ रैखिक; पहला - $\\ln[A]$ बनाम $t$ रैखिक (ढलान $-k$); दूसरा - $1/[A]$ बनाम $t$ रैखिक। **रेडियोधर्मी क्षय** पहला क्रम है: $[A]$, $t$, औसत जीवन $\\ln[A]$।',
            },
            {
              english: '**Radioactive decay** is first order: $N = N_0 e^{-\\lambda t}$, $t_{1/2} = 0.693/\\lambda$, mean life $\\tau = 1/\\lambda$.',
        hindi: '**रेडियोधर्मी क्षय** प्रथम क्रम है: $N = N_0 e^{-\\lambda t}$, $t_{1/2} = 0.693/\\lambda$, औसत जीवन $\\tau = 1/\\lambda$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A first-order reaction is 75 % complete in 60 min. Find $k$ and $t_{1/2}$.',
        hindi: 'प्रथम-क्रम की प्रतिक्रिया 60 मिनट में 75% पूरी हो जाती है। $k$ और $t_{1/2}$ खोजें।',
          },
          solution: {
            english: `$k = (2.303/60)\\log(1/0.25) = (2.303\\times 0.602)/60 = 0.0231$ min⁻¹.
$t_{1/2} = 0.693/0.0231 = 30$ min.`,
            hindi: '$k = (2.303/60)\\log(1/0.25) = (2.303\\times 0.602)/60 = 0.0231$ मिनट⁻¹। $t_{1/2} = 0.693/0.0231 = 30$ मिनट।',
          },
        },
      ],
    },
    {
      id: 'temperature',
      title: {
        english: 'Temperature Dependence — Arrhenius Equation',
        hindi: 'ताप निर्भरता — आर्हेनियस समीकरण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Arrhenius equation',
        hindi: 'अरहेनियस समीकरण',
          },
          statement: {
            english: '$k = A\\,e^{-E_a/RT}$. Logarithmic form: $\\ln k = \\ln A - E_a/(RT)$. Plot of $\\ln k$ vs $1/T$ is linear with slope $-E_a/R$ and intercept $\\ln A$.',
        hindi: '$k = A\\,e^{-E_a/RT}$. लघुगणक प्रपत्र: $\\ln k = \\ln A - E_a/(RT)$. $\\ln k$ बनाम $1/T$ का प्लॉट ढलान $-E_a/R$ और अवरोधन $\\ln A$ के साथ रैखिक है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '$A$ = pre-exponential / frequency factor (related to collision rate).',
        hindi: '$A$ = पूर्व-घातीय/आवृत्ति कारक (टकराव दर से संबंधित)।',
            },
            {
              english: '$E_a$ = activation energy: minimum extra energy needed (above mean) for reactant molecules to form the **activated complex / transition state**.',
        hindi: '$E_a$ = सक्रियण ऊर्जा: **सक्रिय जटिल/संक्रमण अवस्था** बनाने के लिए अभिकारक अणुओं के लिए आवश्यक न्यूनतम अतिरिक्त ऊर्जा (माध्य से ऊपर)।',
            },
            {
              english: 'Two-temperature form: $\\log\\dfrac{k_2}{k_1} = \\dfrac{E_a}{2.303\\,R}\\Big(\\dfrac{T_2 - T_1}{T_1 T_2}\\Big)$.',
        hindi: 'दो-तापमान प्रपत्र: $\\log\\dfrac{k_2}{k_1} = \\dfrac{E_a}{2.303\\,R}\\Big(\\dfrac{T_2 - T_1}{T_1 T_2}\\Big)$।',
            },
            {
              english: '**Rule of thumb**: for many reactions, rate roughly doubles for each 10 K rise in $T$ near room temperature ($E_a \\sim 50$ kJ/mol).',
        hindi: '**सामान्य नियम**: कई प्रतिक्रियाओं के लिए, कमरे के तापमान के निकट $T$ ($E_a \\sim 50$ kJ/mol) में प्रत्येक 10 K वृद्धि के लिए दर लगभग दोगुनी हो जाती है।',
            },
            {
              english: 'Catalyst lowers $E_a$ for both forward and reverse reactions equally — does not shift equilibrium.',
        hindi: 'उत्प्रेरक आगे और पीछे दोनों प्रतिक्रियाओं के लिए $E_a$ को समान रूप से कम करता है - संतुलन नहीं बदलता है।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Rate of a reaction doubles when $T$ rises from 300 K to 310 K. Find $E_a$.',
        hindi: 'जब $T$ 300 K से 310 K तक बढ़ जाता है तो प्रतिक्रिया की दर दोगुनी हो जाती है। $E_a$ खोजें। **टकराव सिद्धांत** (द्विआण्विक): दर $T$ जहां $E_a$ = टक्कर आवृत्ति, = स्थैतिक कारक (अभिविन्यास)।',
          },
          solution: {
            english: `$\\log 2 = (E_a/2.303 R)(10/(300\\cdot 310))$.
$0.301 = (E_a/19.15)(1.075\\times 10^{-4})$ ⇒ $E_a \\approx 53.6$ kJ/mol.`,
            hindi: '$\\log 2 = (E_a/2.303 R)(10/(300\\cdot 310))$। $0.301 = (E_a/19.15)(1.075\\times 10^{-4})$ ⇒ $E_a \\approx 53.6$ kJ/mol.',
          },
        },
      ],
    },
    {
      id: 'collision',
      title: {
        english: 'Collision Theory & Mechanisms',
        hindi: 'संघट्ट सिद्धान्त और क्रियाविधियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Collision theory** (bimolecular): rate $= P\\,Z_{AB}\\,e^{-E_a/RT}$ where $Z_{AB}$ = collision frequency, $P$ = steric factor (orientation).',
        hindi: '**टकराव सिद्धांत** (द्विआणविक): दर $= P\\,Z_{AB}\\,e^{-E_a/RT}$ जहां $Z_{AB}$ = टक्कर आवृत्ति, $P$ = स्थैतिक कारक (अभिविन्यास)।',
            },
            {
              english: 'Effective collisions need (i) sufficient energy ($\\ge E_a$) and (ii) proper orientation.',
        hindi: 'प्रभावी टकराव के लिए (i) पर्याप्त ऊर्जा ($\\ge E_a$) और (ii) उचित अभिविन्यास की आवश्यकता होती है।',
            },
            {
              english: 'Transition-state theory: forms an unstable activated complex of higher energy than reactants/products. $\\Delta H_{rxn} = E_{a,fwd} - E_{a,bwd}$.',
        hindi: 'संक्रमण-अवस्था सिद्धांत: reactants/products से अधिक ऊर्जा का एक अस्थिर सक्रिय परिसर बनाता है। $\\Delta H_{rxn} = E_{a,fwd} - E_{a,bwd}$.',
            },
            {
              english: 'For multi-step: rate law derived by **rate-determining step** assumption + **steady-state** for intermediates if needed.',
        hindi: 'बहु-चरण के लिए: यदि आवश्यक हो तो मध्यवर्ती के लिए **दर-निर्धारण चरण** धारणा + **स्थिर-स्थिति** द्वारा प्राप्त दर कानून।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Rate} = k[A]^x[B]^y',
      desc: {
        english: 'General rate law',
        hindi: 'सामान्य दर कानून',
      },
    },
    {
      latex: 'k = \\dfrac{2.303}{t}\\log\\dfrac{[A]_0}{[A]}',
      desc: {
        english: 'First-order $k$',
        hindi: 'पहला ऑर्डर $k$',
      },
    },
    {
      latex: 't_{1/2}^{1\\text{st}} = 0.693/k',
      desc: {
        english: 'First-order half-life',
        hindi: 'प्रथम-क्रम अर्ध-जीवन',
      },
    },
    {
      latex: 't_{1/2}^{0} = [A]_0/(2k),\\ t_{1/2}^{2}=1/(k[A]_0)',
      desc: {
        english: 'Other half-lives',
        hindi: 'अन्य अर्ध-जीवन',
      },
    },
    {
      latex: 'k = A e^{-E_a/RT}',
      desc: {
        english: 'Arrhenius',
        hindi: 'अरहेनियस',
      },
    },
    {
      latex: '\\log\\dfrac{k_2}{k_1} = \\dfrac{E_a}{2.303 R}\\dfrac{T_2-T_1}{T_1 T_2}',
      desc: {
        english: 'Two-T form',
        hindi: 'टू-टी फॉर्म',
      },
    },
    {
      latex: '\\Delta H = E_{a,f} - E_{a,b}',
      desc: {
        english: 'Forward/backward $E_a$',
        hindi: 'Forward/backward $E_a$',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Half-life',
      problem: {
        english: 'For a first-order reaction $t_{1/2} = 23.1$ min. Time required for 99 % completion?',
        hindi: 'प्रथम-क्रम प्रतिक्रिया के लिए $t_{1/2} = 23.1$ मिनट। 99% पूर्णता के लिए समय की आवश्यकता है?',
      },
      answer: {
        english: '≈ 153.5 min',
        hindi: '≈ 153.5 मिनट',
      },
      solution: {
        english: '$k = 0.693/23.1 = 0.030$ min⁻¹; $t = 2.303/k \\log(100/1) = 2.303/0.030 \\times 2 = 153.5$ min.',
        hindi: '$k = 0.693/23.1 = 0.030$ मिनट⁻¹; $t = 2.303/k \\log(100/1) = 2.303/0.030 \\times 2 = 153.5$ मिनट।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Order',
      problem: {
        english: 'Units of $k$ for second-order reaction:',
        hindi: 'दूसरे क्रम की प्रतिक्रिया के लिए $k$ की इकाइयाँ:',
      },
      answer: {
        english: 'L·mol⁻¹·s⁻¹',
        hindi: 'L·mol⁻¹·s⁻¹',
      },
      solution: {
        english: 'Rate $=$ mol/L·s; $k = $ rate / [A]² ⇒ L/mol·s.',
        hindi: 'दर $=$ mol/L·s; $k = $ दर / [ए]² ⇒ एल/मोल·एस।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Arrhenius',
      problem: {
        english: 'A reaction rate doubles when $T$ rises 10 K (from 300 to 310 K). $E_a$?',
        hindi: 'जब $T$ 10 K (300 से 310 K) बढ़ जाता है तो प्रतिक्रिया दर दोगुनी हो जाती है। $E_a$?',
      },
      answer: {
        english: '≈ 53 kJ/mol',
        hindi: '≈ 53 kJ/mol',
      },
      solution: {
        english: 'See worked example.',
        hindi: 'कार्यशील उदाहरण देखें।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Pseudo',
      problem: {
        english: 'Hydrolysis of ester in dilute aqueous HCl is termed:',
        hindi: 'तनु जलीय HCl में एस्टर के हाइड्रोलिसिस को कहा जाता है:',
      },
      answer: {
        english: 'Pseudo-first-order',
        hindi: 'छद्म प्रथम क्रम',
      },
      solution: {
        english: 'Water in large excess, $[H_2O]$ constant.',
        hindi: 'अत्यधिक मात्रा में पानी, $[H_2O]$ स्थिरांक।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Catalyst',
      problem: {
        english: 'A catalyst increases rate by:',
        hindi: 'एक उत्प्रेरक दर बढ़ाता है:',
      },
      answer: {
        english: 'Lowering activation energy',
        hindi: 'सक्रियण ऊर्जा कम करना',
      },
      solution: {
        english: 'Provides alternate pathway; does not change $\\Delta H$ or $K$.',
        hindi: 'वैकल्पिक मार्ग प्रदान करता है; $\\Delta H$ या $K$ नहीं बदलता।',
      },
    },
  ],
  next: {
    slug: 'surface-chemistry',
    title: 'Surface Chemistry',
  },
};

export default chapter;
